import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Timeline, DataSet } from 'vis-timeline/standalone'
import toast from 'react-hot-toast'
import {
  FaPlus,
  FaTimes,
  FaCalendarAlt,
  FaFilter,
  FaPills,
  FaExpand
} from 'react-icons/fa'
import { FaMagnifyingGlassPlus, FaMagnifyingGlassMinus } from 'react-icons/fa6'
import CustomButton from '../../../shared/CustomButton'
import { fetchData } from '../../../helpers/fetchData'
import { useTranslation } from 'react-i18next'
const MedicationTab = ({ patient }) => {
  const { t } = useTranslation()
  // Sample patients data
  const [patients] = useState([
    { id: 1, name: patient?.name || 'John Doe' },
    { id: 2, name: 'Jane Smith' },
    { id: 3, name: 'Bob Johnson' },
    { id: 4, name: 'Alice Brown' }
  ])
  const [emrItems, setEmrItems] = useState([])
  // Sample medication data
  const [medications, setMedications] = useState([
    {
      id: 1,
      name: 'Paracetamol 500mg',
      category: 'Regular Non-Injectable',
      dosage: '500mg twice daily',
      route: 'Oral',
      schedule: '8:00 AM, 8:00 PM',
      status: 'Active',
      date: '2025-06-25',
      duration: '7 days',
      patientId: 1,
      patientName: patient?.name || 'John Doe',
      startDate: new Date('2025-06-25'),
      endDate: new Date('2025-07-02')
    },
    {
      id: 2,
      name: 'Amoxicillin 250mg',
      category: 'Antimicrobial',
      dosage: '250mg three times daily',
      route: 'Oral',
      schedule: '8:00 AM, 1:00 PM, 8:00 PM',
      status: 'Active',
      date: '2025-06-26',
      duration: '10 days',
      patientId: 1,
      patientName: patient?.name || 'John Doe',
      startDate: new Date('2025-06-26'),
      endDate: new Date('2025-07-06')
    },
    {
      id: 3,
      name: 'Insulin Injection',
      category: 'Regular Injewctable',
      dosage: '10 units subcutaneous',
      route: 'Subcutaneous',
      schedule: 'Before meals',
      status: 'Stopped',
      date: '2025-06-20',
      duration: 'Ongoing',
      patientId: 1,
      patientName: patient?.name || 'John Doe',
      startDate: new Date('2025-06-20'),
      endDate: new Date('2025-06-25')
    }
  ])

  // Form state
  const [showMedicationForm, setShowMedicationForm] = useState(false)
  const [newMedication, setNewMedication] = useState({
    name: '',
    category: '',
    dosage: '',
    route: '',
    schedule: '',
    status: 'Active',
    date: new Date().toISOString().split('T')[0],
    duration: '',
    patientId: 1,
    patientName: patient?.full_name || 'John Doe'
  })

  // Filter state
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    status: '',
    category: ''
  })

  // Timeline references
  const timelineRef = useRef(null)
  const timelineContainer = useRef(null)

  // Category options
  const categoryOptions = [
    'Anticoagulants & Antiplatelets',
    'Antimicrobial',
    'Regular Injewctable',
    'Regular Non-Injectable',
    'Oxygen',
    'As Required',
    'Once Only'
  ]

  const statusOptions = ['Active', 'Stopped', 'Home']

  // Status colors
  const getStatusColor = status => {
    switch (status) {
      case 'Active':
        return '#10B981' // Green
      case 'Stopped':
        return '#EF4444' // Red
      case 'Home':
        return '#F59E0B' // Amber
      default:
        return '#6B7280' // Gray
    }
  }

  // Category colors
  const getCategoryColor = category => {
    const colors = {
      'Anticoagulants & Antiplatelets': '#8B5CF6', // Purple
      Antimicrobial: '#3B82F6', // Blue
      'Regular Injewctable': '#EF4444', // Red
      'Regular Non-Injectable': '#10B981', // Green
      Oxygen: '#06B6D4', // Cyan
      'As Required': '#F59E0B', // Amber
      'Once Only': '#EC4899' // Pink
    }
    return colors[category] || '#6B7280'
  }

  // Filter medications with useMemo to prevent unnecessary re-renders
  const filteredMedications = useMemo(() => {
    const { status, category, date } = filters
    console.log(medications)

    return medications?.filter(med => {
      if (status && med.status !== status) return false
      if (category && med.category !== category) return false
      // For date filter, show medications that are active on the selected date
      if (date) {
        const filterDate = new Date(date)
        const startDate = new Date(med.startDate)
        const endDate = new Date(med.endDate)
        if (filterDate < startDate || filterDate > endDate) return false
      }
      return true
    })
  }, [medications, filters])
  console.log(medications)

  // Initialize timeline - only create once
  useEffect(() => {
    if (!timelineContainer.current || timelineRef.current) return

    // Timeline options
    const options = {
      groupOrder: 'content',
      orientation: 'top',
      zoomable: true,
      moveable: true,
      showMajorLabels: true,
      showMinorLabels: true,
      format: {
        minorLabels: {
          hour: 'HH:mm',
          day: 'DD'
        },
        majorLabels: {
          hour: 'ddd DD/MM',
          day: 'MMMM YYYY'
        }
      },
      timeAxis: { scale: 'hour', step: 6 }, // 6-hour intervals (quarters)
      start: new Date(Date.now() - 24 * 60 * 60 * 1000), // Yesterday
      end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Next week
      autoResize: true,
      stack: false,
      margin: {
        item: {
          horizontal: 10,
          vertical: 5
        }
      }
    }

    try {
      // Create timeline with empty initial data
      timelineRef.current = new Timeline(
        timelineContainer.current,
        new DataSet([]),
        new DataSet([]),
        options
      )

      // Add custom styles only once
      const styleId = 'medication-timeline-styles'
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `
          .vis-timeline {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            font-family: Arial, sans-serif;
          }
          .vis-item {
            border-radius: 4px;
            opacity: 0.8;
          }
          .vis-item:hover {
            opacity: 1;
          }
          .vis-item.vis-selected {
            border-width: 2px;
            opacity: 1;
          }
          .medication-row {
            padding: 8px;
            border-left: 4px solid transparent;
            min-height: 60px;
          }
          .medication-group-active {
            border-left-color: #10B981 !important;
          }
          .medication-group-stopped {
            border-left-color: #EF4444 !important;
          }
          .medication-group-home {
            border-left-color: #F59E0B !important;
          }
          .vis-labelset .vis-label {
            border-right: 1px solid #e5e7eb;
            min-height: 60px;
          }
          .vis-time-axis .vis-text {
            color: #374151;
          }
          .vis-time-axis .vis-grid.vis-major {
            border-left: 1px solid #d1d5db;
          }
          .vis-time-axis .vis-grid.vis-minor {
            border-left: 1px solid #f3f4f6;
          }
        `
        document.head.appendChild(style)
      }
    } catch (error) {
      console.error('Error creating timeline:', error)
    }

    // Cleanup function - only destroy on unmount
    return () => {
      if (timelineRef.current) {
        try {
          timelineRef.current.destroy()
        } catch (error) {
          console.warn('Error destroying timeline:', error)
        }
        timelineRef.current = null
      }
    }
  }, [filteredMedications]) // Only run once on mount

  // Update timeline data when filtered medications change
  useEffect(() => {
    if (!timelineRef.current) return

    try {
      // Create groups (rows) for each medication
      const groups = new DataSet(
        filteredMedications.map(med => ({
          id: med.id,
          content: `
            <div class="medication-row">
              <div class="medication-header">
                <div class="medication-name" style="font-weight: bold; color: ${getCategoryColor(
                  med.category
                )};">
                  ${med.name}
                </div>
                <div class="medication-status" style="color: ${getStatusColor(
                  med.status
                )}; font-size: 12px;">
                  ${med.status}
                </div>
              </div>
              <div class="medication-details" style="font-size: 11px; color: #666;">
                <div>${med.category}</div>
                <div>${med.dosage}</div>
                <div>Route: ${med.route}</div>
                <div>Schedule: ${med.schedule}</div>
              </div>
            </div>
          `,
          className: `medication-group-${med.status.toLowerCase()}`
        }))
      )
      const items = new DataSet(
        filteredMedications.map(med => ({
          id: med.id,
          group: med.id,
          start: med.startDate,
          end: med.endDate,
          content: med.name,
          style: `background-color: ${getCategoryColor(
            med.category
          )}; border-color: ${getCategoryColor(med.category)};`,
          className: `medication-item-${med.status.toLowerCase()}`,
          title: `${med.name}\nCategory: ${med.category}\nStatus: ${med.status}\nDuration: ${med.duration}\nDosage: ${med.dosage}`
        }))
      )

      timelineRef.current.setGroups(groups)
      timelineRef.current.setItems(items)
    } catch (error) {
      console.warn('Error updating timeline data:', error)
    }
  }, [filteredMedications])

  const handleAddMedication = () => {
    if (!newMedication.name || !newMedication.category) {
      toast.error('Please fill in all required fields')
      return
    }

    const startDate = new Date(newMedication.date)
    let endDate = new Date(startDate)

    // Calculate end date based on duration
    if (newMedication.duration && newMedication.duration !== 'Ongoing') {
      const durationMatch = newMedication.duration.match(
        /(\d+)\s*(day|days|week|weeks|month|months)/i
      )
      if (durationMatch) {
        const value = parseInt(durationMatch[1])
        const unit = durationMatch[2].toLowerCase()

        if (unit.startsWith('day')) {
          endDate.setDate(startDate.getDate() + value)
        } else if (unit.startsWith('week')) {
          endDate.setDate(startDate.getDate() + value * 7)
        } else if (unit.startsWith('month')) {
          endDate.setMonth(startDate.getMonth() + value)
        }
      } else {
        // Default to 7 days if duration format is not recognized
        endDate.setDate(startDate.getDate() + 7)
      }
    } else {
      // Default end date for ongoing medications
      endDate.setDate(startDate.getDate() + 30)
    }

    const medication = {
      id: Date.now(),
      ...newMedication,
      startDate: startDate,
      endDate: endDate
    }

    setMedications([...medications, medication])
    setNewMedication({
      name: '',
      category: '',
      dosage: '',
      route: '',
      schedule: '',
      status: 'Active',
      date: new Date().toISOString().split('T')[0],
      duration: '',
      patientId: patient?.patient_id || 1,
      patientName: patient?.full_name || 'John Doe'
    })
    setShowMedicationForm(false)
    toast.success('Medication added successfully')
  }

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value })
  }

  const handleZoomIn = () => {
    if (timelineRef.current) {
      try {
        timelineRef.current.zoomIn(0.2)
      } catch (error) {
        console.warn('Error zooming in:', error)
      }
    }
  }

  const handleZoomOut = () => {
    if (timelineRef.current) {
      try {
        timelineRef.current.zoomOut(0.2)
      } catch (error) {
        console.warn('Error zooming out:', error)
      }
    }
  }

  const handleFitToWindow = () => {
    if (timelineRef.current) {
      try {
        timelineRef.current.fit()
      } catch (error) {
        console.warn('Error fitting to window:', error)
      }
    }
  }
  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const res = await fetchData(
          'POST',
          '/v3/emr/getMedicationByPatientId',
          {
            patientID: patient?.patient_id,
            page: 1,
            perPage: 1000
          }
        )

        if (res) {
          const allMedications = Array.isArray(res.data) ? res.data.flatMap(emrRecord =>
            Array.isArray(emrRecord.items) ?
            emrRecord.items.map(item => ({
              name: item.item_name,
              code: item.standard_code,
              description: item.standard_description,
              dosage: item.dosage || '500mg twice daily',
              route: item.route || 'Oral',
              frequency: item.frequency || '',
              startDate:
                item.service_start_date ||
                new Date().toISOString().split('T')[0],
              endDate:
                item.service_start_date ||
                new Date().toISOString().split('T')[0],
              price: item.unit_price,
              category: item.item_type || 'medication',
              schedule: item.frequency || '',
              status: 'Active',
              date: new Date().toISOString().split('T')[0],
              duration: item.duration || '7 days',
              patientId: patient?.patient_id || 1,
              patientName: patient?.full_name || 'John Doe'
            }))
            : []

          ) : []
          if (allMedications.length > 0) {
             setMedications(prev => [...prev, ...allMedications])
           }
        }
      } catch (error) {
        console.error('Error fetching medications:', error)
      }
    }

    fetchMedications()
  }, [patient?.patient_id, patient?.full_name])

  return (
    <div className='h-full flex flex-col'>
      {/* Header */}
      <div className='p-6 pb-4 flex-shrink-0'>
        <div className='flex items-center justify-between mb-6'>
          <h2 className='text-2xl font-bold'>{t('Patient Medication Timeline')}</h2>
        </div>

        {/* Filters and Controls */}
        <div className='flex items-center space-x-4 mb-6 flex-wrap'>
          {/* Date Filter */}
          <div className='flex items-center space-x-2'>
            <FaCalendarAlt className='text-gray-500' />
            <label className='text-sm font-medium text-gray-700'>{t('Date')}:</label>
            <input
              type='date'
              value={filters.date}
              onChange={e => handleFilterChange('date', e.target.value)}
              className='border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          {/* Status Filter */}
          <div className='flex items-center space-x-2'>
            <FaFilter className='text-gray-500' />
            <label className='text-sm font-medium text-gray-700'>{t('Status')}:</label>
            <select
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              className='border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              <option value=''>{t('All Statuses')}</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>
                  {t(status)}
                </option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div className='flex items-center space-x-2'>
            <FaPills className='text-gray-500' />
            <label className='text-sm font-medium text-gray-700'>
              {t('Category')}:
            </label>
            <select
              value={filters.category}
              onChange={e => handleFilterChange('category', e.target.value)}
              className='border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              <option value=''>{t('All Categories')}</option>
              {categoryOptions.map(category => (
                <option key={category} value={category}>
                  {t(category)}
                </option>
              ))}
            </select>
          </div>

          {/* Add Medication Button */}
          <CustomButton
            onClick={() => setShowMedicationForm(true)}
            label={t('Add Medication')}
            icon={<FaPlus size={14} />}
            buttonType='primary'
            className='!h-[40px]'
          />

          {/* Timeline Controls */}
          <div className='flex items-center space-x-2 ml-auto'>
            <CustomButton
              onClick={handleZoomIn}
              icon={<FaMagnifyingGlassPlus size={14} />}
              buttonType='reset'
              className='!h-[40px] !w-[40px] !p-0'
              title='Zoom In'
            />
            <CustomButton
              onClick={handleZoomOut}
              icon={<FaMagnifyingGlassMinus size={14} />}
              buttonType='reset'
              className='!h-[40px] !w-[40px] !p-0'
              title='Zoom Out'
            />
            <CustomButton
              onClick={handleFitToWindow}
              icon={<FaExpand size={14} />}
              buttonType='reset'
              className='!h-[40px] !w-[40px] !p-0'
              title='Fit to Window'
            />
          </div>
        </div>
      </div>

      {/* Timeline Container */}
      <div className='flex-1 px-6 pb-6 overflow-y-auto h-full max-h-[calc(100vh-250px)]'>
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col overflow-y-auto h-full max-h-[calc(100vh-250px)]'>
          <div className='flex items-center justify-between mb-4 flex-shrink-0 overflow-y-auto h-full max-h-[calc(100vh-250px)]'>
            <h3 className='text-lg font-semibold text-gray-900'>
              {t('Medication Timeline')}
            </h3>
            <div className='text-sm text-gray-600'>
              {filteredMedications.length} {t('medication(s) displayed')}
            </div>
          </div>

          {filteredMedications.length > 0 ? (
            <div
              ref={timelineContainer}
              className='timeline-container overflow-y-auto h-full max-h-[calc(100vh-250px)]'
              style={{ height: '100%', maxHeight: 'calc(100vh - 250px)' }}
            />
          ) : (
            <div className='flex items-center justify-center h-full text-gray-500'>
              <div className='text-center'>
                <FaPills size={64} className='mx-auto mb-4 text-gray-300' />
                <p className='text-lg mb-2'>{t('No medications found')}</p>
                <p>
                  {t('Add a medication or adjust your filters to see the timeline.')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Medication Modal */}
      {showMedicationForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
            <div className='flex items-center justify-between mb-6'>
              <h3 className='text-lg font-semibold text-gray-900'>
                {t('Add New Medication')}
              </h3>
              <button
                onClick={() => setShowMedicationForm(false)}
                className='text-gray-400 hover:text-gray-600'
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('Name')}*
                </label>
                <input
                  type='text'
                  value={newMedication.name}
                  onChange={e =>
                    setNewMedication({ ...newMedication, name: e.target.value })
                  }
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  placeholder='Medication name'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('Category')}*
                </label>
                <select
                  value={newMedication.category}
                  onChange={e =>
                    setNewMedication({
                      ...newMedication,
                      category: e.target.value
                    })
                  }
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value=''>{t('Select Category')}</option>
                  {categoryOptions.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('Status')}
                </label>
                <select
                  value={newMedication.status}
                  onChange={e =>
                    setNewMedication({
                      ...newMedication,
                      status: e.target.value
                    })
                  }
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>
                      {t(status)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('Date')}
                </label>
                <input
                  type='date'
                  value={newMedication.date}
                  onChange={e =>
                    setNewMedication({ ...newMedication, date: e.target.value })
                  }
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('Duration')}
                </label>
                <input
                  type='text'
                  value={newMedication.duration}
                  onChange={e =>
                    setNewMedication({
                      ...newMedication,
                      duration: e.target.value
                    })
                  }
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  placeholder='e.g., 7 days, 2 weeks, Ongoing'
                />
              </div>

              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('Dosage')}
                </label>
                <textarea
                  value={newMedication.dosage}
                  onChange={e =>
                    setNewMedication({
                      ...newMedication,
                      dosage: e.target.value
                    })
                  }
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  rows='3'
                  placeholder='Dosage instructions'
                />
              </div>

              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                 {t('Route')}
                </label>
                <textarea
                  value={newMedication.route}
                  onChange={e =>
                    setNewMedication({
                      ...newMedication,
                      route: e.target.value
                    })
                  }
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  rows='2'
                  placeholder='Route of administration'
                />
              </div>

              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('Schedule')}
                </label>
                <textarea
                  value={newMedication.schedule}
                  onChange={e =>
                    setNewMedication({
                      ...newMedication,
                      schedule: e.target.value
                    })
                  }
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  rows='2'
                  placeholder='Administration schedule'
                />
              </div>
              {console.log(patient)}
              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('Select Patient')}
                </label>
                <select
                  value={newMedication.patientId}
                  onChange={e => {
                    const selectedPatient = patients.find(
                      p => p.id === parseInt(e.target.value)
                    )
                    setNewMedication({
                      ...newMedication,
                      patientId: parseInt(e.target.value),
                      patientName: selectedPatient?.full_name || ''
                    })
                  }}
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  disabled
                >
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.full_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className='flex items-center justify-end space-x-3 mt-6'>
              <CustomButton
                onClick={() => setShowMedicationForm(false)}
                label={t('Cancel')}
                buttonType='reset'
                className='!h-[40px]'
              />
              <CustomButton
                onClick={handleAddMedication}
                label={t('Add Medication')}
                icon={<FaPlus size={14} />}
                buttonType='primary'
                className='!h-[40px]'
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicationTab
